/* eslint-disable class-methods-use-this */
import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import agentAwareUtils from 'utils/AgentAware';

import { Flex } from 'components/ui';
import CheckoutButton from 'components/RwdBasket/RwdBasketLayout/CostSummary/Buttons/CheckoutButton';
import PayPalButton from 'components/PayPalButton/PayPalButton';
import PayPalCheckout from 'utils/PayPalCheckout';
import PayPalCheckoutSdkButton from 'components/RwdBasket/RwdBasketLayout/CostSummary/Buttons/PayPalCheckoutSdkButton';
import ApplePayButton from 'components/ApplePayButton/ApplePayButton';
import ApplePay from 'services/ApplePay';
import GooglePayButton from 'components/GooglePayButton/GooglePayButton';
import GooglePay from 'utils/GooglePay';
import VenmoExpressButton from 'components/VenmoExpressButton/VenmoExpressButton';
import basketUtils from 'utils/Basket';

import { lineHeights, space } from 'style/config';

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[3]
    },
    alternateRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: space[3],
        width: '100%'
    },
    button: {
        display: 'flex',
        flex: 1,
        // Allow each flex slot to shrink below the width of its injected
        // payment-button content
        minWidth: 0
    },
    checkoutButton: {
        lineHeight: lineHeights.tight,
        display: 'flex',
        width: '100%'
    }
};

class ButtonGrid extends BaseClass {
    static MAX_BUTTONS = 3;

    constructor(props) {
        super(props);

        this.state = {
            isApplePayPayment: false,
            isGooglePayPayment: false
        };
    }

    componentDidMount() {
        this.props.getApplePaymentStatus().then(isApplePayPayment => this.setState({ isApplePayPayment }));
        this.props.getGooglePaymentStatus?.().then(isGooglePayPayment => this.setState({ isGooglePayPayment }));
    }

    componentDidUpdate() {
        this.props.getApplePaymentStatus().then(isApplePayPayment => {
            if (isApplePayPayment !== this.state.isApplePayPayment) {
                this.setState({ isApplePayPayment });
            }
        });
        this.props.getGooglePaymentStatus?.().then(isGooglePayPayment => {
            if (isGooglePayPayment !== this.state.isGooglePayPayment) {
                this.setState({ isGooglePayPayment });
            }
        });
    }

    render() {
        const {
            // Fall back to basketUtils.isPickup() when prop is omitted so the BOPIS-only killswitch
            // does not silently route BOPIS orders to the legacy PayPal flow.
            isBopis = basketUtils.isPickup(),
            isPaypalPayment,
            isVenmoEligible,
            isCheckoutDisabled,
            isSignedIn,
            isSDDBasketAvailable,
            isSDUOnlyInBasket,
            isRougeRewardsApplied,
            hideCheckoutButton,
            transactionAmount,
            bopisGooglePayTransactionAmount
        } = this.props;

        const { isApplePayPayment, isGooglePayPayment } = this.state;
        const hasSDDItem = isSDDBasketAvailable && !isSDUOnlyInBasket;

        const { container, alternateRow, button, checkoutButton } = styles;

        if (hideCheckoutButton) {
            return null;
        }

        const isApplePayAvailable = Boolean(isApplePayPayment) && isApplePayPayment !== ApplePay.TYPES.HIDDEN;
        const isGooglePayAvailable = Boolean(isGooglePayPayment) && isGooglePayPayment !== GooglePay.TYPES.HIDDEN;
        const payPalCheckoutEnabled = PayPalCheckout.isPayPalCheckoutEnabledForLocale();

        // BOPIS paysheet cannot be updated post-open, so prefer the
        // resolver-picked total; fall back to the merchandise subtotal.
        const googlePayTransactionAmount = isBopis && bopisGooglePayTransactionAmount ? bopisGooglePayTransactionAmount : transactionAmount;

        const googlePayButton = (
            <GooglePayButton
                className={agentAwareUtils.applyHideAgentAwareClass()}
                key='gpay'
                isGooglePayPayment={isGooglePayPayment}
                isBopis={isBopis}
                isSdd={hasSDDItem}
                transactionAmount={googlePayTransactionAmount}
                css={button}
            />
        );

        const applePayButton = (
            <ApplePayButton
                key='applepay'
                isBopis={isBopis}
                isApplePayPayment={isApplePayPayment}
                iconOnly
                css={button}
            />
        );

        const venmoButton = (
            <VenmoExpressButton
                key='venmo'
                className={agentAwareUtils.applyHideAgentAwareClass()}
                isBopis={isBopis}
                isVenmoEligible={isVenmoEligible}
                css={button}
            />
        );

        // The key flips with isBopis so a basket type change forces React to
        // unmount the previous instance and mount a fresh one — the new component
        // owns the SDK render/cleanup lifecycle, no prop diffing required here.
        const payPalButton = payPalCheckoutEnabled ? (
            <PayPalCheckoutSdkButton
                key={`paypal-sdk-${isBopis ? 'bopis' : 'sth'}`}
                isBopis={isBopis}
            />
        ) : (
            <PayPalButton
                key='paypal'
                className={agentAwareUtils.applyHideAgentAwareClass()}
                isBopis={isBopis}
                isPaypalPayment={isPaypalPayment}
                css={button}
            />
        );

        // Priority is conditional on Apple Pay availability, and the row shows at most 3 buttons
        const candidates = isApplePayAvailable
            ? [
                { node: applePayButton, eligible: isApplePayAvailable },
                { node: payPalButton, eligible: isPaypalPayment },
                { node: googlePayButton, eligible: isGooglePayAvailable },
                { node: venmoButton, eligible: isVenmoEligible }
            ]
            : [
                { node: googlePayButton, eligible: isGooglePayAvailable },
                { node: payPalButton, eligible: isPaypalPayment },
                { node: venmoButton, eligible: isVenmoEligible }
            ];

        const alternateButtons = candidates
            .filter(candidate => candidate.eligible)
            .slice(0, ButtonGrid.MAX_BUTTONS)
            .map(candidate => candidate.node);

        return (
            <Flex css={container}>
                <CheckoutButton
                    isBopis={isBopis}
                    isCheckoutDisabled={isCheckoutDisabled}
                    isSignedIn={isSignedIn}
                    hasSDDItem={hasSDDItem}
                    isRougeRewardsApplied={isRougeRewardsApplied}
                    css={checkoutButton}
                />
                {alternateButtons.length > 0 && (
                    <Flex
                        data-at={Sephora.debug.dataAt('basket_alternate_payments_row')}
                        css={alternateRow}
                    >
                        {alternateButtons}
                    </Flex>
                )}
            </Flex>
        );
    }
}

export default wrapComponent(ButtonGrid, 'ButtonGrid', true);
